import { useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  deleteRow,
  fetchJobPhotos,
  signJobPhotos,
  updateRow,
  uploadJobPhoto,
  type JobPhoto,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Photographs of the customer's own roof, for the proposal.
 *
 * A customer looking at a quote for a roof they cannot see is being asked to
 * take somebody's word for it. Four photographs of their own valley, their own
 * flashing and their own rotten fascia is the entire argument — and the rep
 * already has them on the phone they are holding.
 *
 * Jonathon, 2026-08-13: "i just need an upload button and i can handle the
 * rest." So: an upload button, thumbnails in the order they will appear, a
 * caption on each, and a way to take one out. `capture` is deliberately not
 * set, so the button offers the camera roll as well as the camera — most of
 * these were taken on the roof an hour earlier.
 */
export function ProposalPhotos({ jobId }: { jobId: string }) {
  const { profileId } = useSession();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photos = useAsync(() => fetchJobPhotos(jobId), [jobId]);
  const rows = photos.data ?? [];

  /*
   * Links the office can look at, good for an hour.
   *
   * The bucket is private — what the customer eventually sees is signed by the
   * Edge Function that serves their copy — so nothing here can point straight
   * at storage. Re-signed whenever the set of photographs changes.
   */
  const urls = useAsync(
    () => signJobPhotos(rows.map((row) => row.path)),
    [rows.map((row) => row.path).join(",")],
  );

  const add = async (files: FileList) => {
    setBusy(true);
    setError(null);
    try {
      // In the order they were picked, after whatever is already there.
      let next =
        rows.reduce((max, row) => Math.max(max, row.sort_order), 0) + 10;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          throw new Error(t("{name} is not an image.", { name: file.name }));
        }
        await uploadJobPhoto(jobId, file, next, profileId);
        next += 10;
      }
      await photos.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (photo: JobPhoto) => {
    // Kept as a confirmation: this is outside the undo that covers the order
    // editors, and the file itself is not coming back.
    if (!window.confirm(t("Take this photo off the proposal?"))) return;
    try {
      await deleteRow("job_photos", photo.id);
      await photos.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const caption = async (photo: JobPhoto, text: string) => {
    try {
      await updateRow("job_photos", photo.id, { caption: text || null });
      await photos.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="chk-sub">{t("Photos of their roof")}</div>
      <ErrorNote error={error} />

      <div className="uploadrow">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) void add(files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn-ghost"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          {busy ? t("Uploading…") : t("Upload photos")}
        </button>
        <span className="na-hint">
          {rows.length === 0
            ? t("They appear on the customer's copy under “What we found”.")
            : tn(
                rows.length,
                "1 photo, in this order.",
                "{n} photos, in this order.",
              )}
        </span>
      </div>

      {rows.length > 0 && (
        <div className="photogrid">
          {rows.map((photo) => (
            <figure key={photo.id} className="photocard">
              {urls.data?.[photo.path] ? (
                <img src={urls.data[photo.path]} alt={photo.caption ?? ""} />
              ) : (
                <div className="photoplaceholder" />
              )}
              <input
                placeholder={t("Caption (optional)")}
                defaultValue={photo.caption ?? ""}
                onBlur={(e) => {
                  if (e.target.value !== (photo.caption ?? "")) {
                    void caption(photo, e.target.value);
                  }
                }}
              />
              <button
                type="button"
                className="xbtn"
                title={t("Take this photo off the proposal")}
                onClick={() => void remove(photo)}
              >
                ✕
              </button>
            </figure>
          ))}
        </div>
      )}
    </>
  );
}
